import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useUtility } from '../../../context/UtilityContext';
import { createMetadata } from '../../../reducers/MetaData/metadata-actions';
import { useDispatch, useSelector } from 'react-redux';
import CustomBreadcrumb from '../../../components/CustomBreadcrumb';
import styles from './stylesheets/MetadataManagement.module.css';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { CustomButton } from '../../../components/CustomButton';

const MetadataManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { metadataList, loading, error } = useSelector((state) => state.metadata);

  const [isOnboarding, setIsOnboarding] = useState(false);
  const [onboardResponse, setOnboardResponse] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedMetadataForQR, setSelectedMetadataForQR] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const qrCanvasRef = useRef(null);

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return;
      }

      try {
        const data = JSON.parse(event.data);

        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          console.log('WhatsApp Embedded Signup:', data);
          setSessionInfo(data);

          if (data.event === 'FINISH') {
            console.log('Signup Finished:', data.data);
            // optionally refresh metadata list
          } else if (data.event === 'CANCEL') {
            console.warn('Signup Cancelled:', data.data);
          } else if (data.event === 'ERROR') {
            console.error('Signup Error:', data.data);
          }
        }
      } catch (error) {
        console.log('Non JSON Response:', event.data);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const fbLoginCallback = (response) => {
    console.log('Facebook Login Response:', response);

    setOnboardResponse(response);

    if (response?.authResponse?.code) {
      const code = response.authResponse.code;
      console.log('Facebook Auth Code:', code);
      onboardWithFacebookCode(code);
    }
  };

  const onboardWithFacebookCode = async (fbCode) => {
    try {
      setIsOnboarding(true);
      dispatch(createMetadata({ fbCode }));
      //   refreshMetadata();
    } catch (error) {
      console.error(error);
    } finally {
      setIsOnboarding(false);
    }
  };

  const launchWhatsAppSignup = () => {
    if (window.FB) {
      window.FB.login(fbLoginCallback, {
        config_id: '1687720035195268',
        response_type: 'code',
        override_default_response_type: true,
        extras: { version: 'v3' },
      });
    }
  };

  const handleShowQR = async (metadata) => {
    const cleanPhoneNumber = metadata.phoneNumber.replace(/[\s\-\(\)\+]/g, '');
    const url = `https://wa.me/${cleanPhoneNumber}`;

    setSelectedMetadataForQR(metadata);
    setQrCodeUrl(url);
    setQrModalOpen(true);
  };

  useEffect(() => {
    if (qrModalOpen && qrCodeUrl && qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, qrCodeUrl);
    }
  }, [qrModalOpen, qrCodeUrl]);

  return (
    <div className={styles.container}>
      <CustomBreadcrumb
        home={{ label: 'Market Place', url: '/marketplace' }}
        itemslist={[{ label: 'Metadata', url: '#' }]}
      />

      <div className={styles.headerSection}>
        <h3>All Metadata ({metadataList.length})</h3>
        <CustomButton
          label={isOnboarding ? 'Processing...' : 'Login with Facebook'}
          icon="pi pi-facebook"
          varient="filled"
          type={'button'}
          loading={isOnboarding}
          onClick={launchWhatsAppSignup}
        />
      </div>

      {/* Embedded Signup Session Info */}
      {sessionInfo && (
        <div className={styles.responseSection}>
          <h4>WhatsApp Embedded Signup Response</h4>
          <pre className={styles.responseBox}>{JSON.stringify(sessionInfo, null, 2)}</pre>
        </div>
      )}

      {/* Facebook Login SDK Response */}
      {onboardResponse && (
        <div className={styles.responseSection}>
          <h4>Facebook SDK Response</h4>
          <pre className={styles.responseBox}>{JSON.stringify(onboardResponse, null, 2)}</pre>
        </div>
      )}

      <div className={styles.tableWrapper}>
        <DataTable
          value={metadataList}
          rows={5}
          responsiveLayout="scroll"
          stripedRows
          emptyMessage="No metadata found"
        >
          <Column field="id" header="ID" sortable />
          <Column field="name" header="Name" sortable />
          <Column field="phoneNumber" header="Phone" />
          <Column
            header="QR"
            body={(rowData) => (
              <CustomButton
                icon={'pi pi-qrcode'}
                varient="text"
                type={'button'}
                onClick={() => handleShowQR(rowData)}
              />
            )}
          />
          <Column
            header="View"
            body={(rowData) => (
              <CustomButton
                icon="pi pi-eye"
                varient="text"
                type="button"
                onClick={() => navigate(`/metadata/${rowData.id}`)}
              />
            )}
          />
        </DataTable>
      </div>

      {qrModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setQrModalOpen(false)}>
          <div className={styles.qrModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <button className={styles.modalClose} onClick={() => setQrModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className={styles.qrModalBody}>
              <div className={styles.qrCodeContainer}>
                <canvas ref={qrCanvasRef} />
              </div>

              <div className={styles.qrInfo}>
                <p>
                  <strong>Whatsapp Link:</strong>
                </p>
                <div className={styles.telegramLink}>
                  <a href={qrCodeUrl} target="_blank" rel="noopener noreferrer">
                    {qrCodeUrl}
                  </a>
                </div>
                <p className={styles.qrInstructions}>
                  Scan this QR code with your phone's camera or Whatsapp app to open the chat.
                </p>
              </div>
              <div className={styles.qrModalActions}>
                <Button
                  label="Close"
                  onClick={() => setQrModalOpen(false)}
                  className={styles.closeQrButton}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataManagement;
